/**
 * Generic embeddable Builder page.
 * Gebruikt door Scrum / Beweegaanbod / Pakket builders.
 *
 * Props:
 *   - api: object met listPublic() en submit() (workshopBuilderAPI-stijl)
 *   - branding: { kindLabel, title, subtitle, headlineHighlight, headlinePrefix, headlineSuffix,
 *                 ctaLabel, successTitle, successMessage, accentColor (tailwind class shortname zoals 'emerald'),
 *                 testIdPrefix }
 */
import { useState, useEffect, useMemo } from 'react';
import {
  Sparkles, Heart, Apple, Activity, Zap, ArrowRight, ArrowLeft, Check,
  CheckCircle2, Send, Loader2, ChevronRight, Mail, Phone, User, Building, MessageSquare,
  Package, Users, Boxes
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';

const LOGO_ICON = 'https://customer-assets.emergentagent.com/job_buyer-match-26/artifacts/52px3160_zynq_logo_icon_e0dde9b0559f42738516da0422c8a0d0.png';

const ICON_MAP = { Sparkles, Heart, Apple, Activity, Zap, Package, Users, Boxes };
function getIcon(name) { return ICON_MAP[name] || Sparkles; }

export default function GenericBuilderPage({ api, branding }) {
  const {
    kindLabel = 'Builder',
    title = 'Bouw jouw aanbod',
    subtitle = 'Kies een type om mee te starten. Vervolgens stel je zelf de onderdelen samen.',
    headlinePrefix = 'Bouw jouw ',
    headlineHighlight = 'ideale aanbod',
    headlineSuffix = '',
    ctaLabel = 'Start bouwen',
    successTitle = 'Bedankt!',
    successMessage = 'We hebben je aanvraag goed ontvangen. We nemen binnen 1-2 werkdagen contact met je op.',
    accent = 'emerald',
    testIdPrefix = 'builder',
  } = branding;

  // Map accent shortname → tailwind classes (we whitelist a few)
  const A = useMemo(() => {
    const palettes = {
      emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600', text700: 'text-emerald-700', btn: 'bg-emerald-600 hover:bg-emerald-700', bg100: 'bg-emerald-100', border500: 'border-emerald-500', bg50: 'bg-emerald-50', hex: '#10B981' },
      indigo:  { bg: 'bg-indigo-50',  text: 'text-indigo-600',  text700: 'text-indigo-700',  btn: 'bg-indigo-600 hover:bg-indigo-700',  bg100: 'bg-indigo-100',  border500: 'border-indigo-500',  bg50: 'bg-indigo-50',  hex: '#6366F1' },
      blue:    { bg: 'bg-blue-50',    text: 'text-blue-600',    text700: 'text-blue-700',    btn: 'bg-blue-600 hover:bg-blue-700',    bg100: 'bg-blue-100',    border500: 'border-blue-500',    bg50: 'bg-blue-50',    hex: '#3B82F6' },
      amber:   { bg: 'bg-amber-50',   text: 'text-amber-600',   text700: 'text-amber-700',   btn: 'bg-amber-600 hover:bg-amber-700',   bg100: 'bg-amber-100',   border500: 'border-amber-500',   bg50: 'bg-amber-50',   hex: '#F59E0B' },
      pink:    { bg: 'bg-pink-50',    text: 'text-pink-600',    text700: 'text-pink-700',    btn: 'bg-pink-600 hover:bg-pink-700',    bg100: 'bg-pink-100',    border500: 'border-pink-500',    bg50: 'bg-pink-50',    hex: '#EC4899' },
    };
    return palettes[accent] || palettes.emerald;
  }, [accent]);

  const [templates, setTemplates] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [error, setError] = useState('');

  const [step, setStep] = useState(0);
  const [selectedTpl, setSelectedTpl] = useState(null);
  const [selections, setSelections] = useState({});
  const [customInputs, setCustomInputs] = useState({});
  const [contact, setContact] = useState({ name: '', email: '', phone: '', company: '' });
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Force light mode for embed-consistency
  useEffect(() => {
    const root = document.documentElement;
    const hadDark = root.classList.contains('dark');
    root.classList.remove('dark');
    return () => { if (hadDark) root.classList.add('dark'); };
  }, []);

  useEffect(() => {
    api.listPublic()
      .then(res => setTemplates(res.data.templates || []))
      .catch(() => setError('Er ging iets fout bij het laden. Probeer het later opnieuw.'))
      .finally(() => setLoadingList(false));
  }, [api]);

  const handlePickTemplate = (tpl) => {
    setSelectedTpl(tpl);
    setSelections({});
    setCustomInputs({});
    setStep(1);
  };

  const setSelection = (key, value) => setSelections(prev => ({ ...prev, [key]: value }));
  const toggleMulti = (key, value) => {
    setSelections(prev => {
      const current = Array.isArray(prev[key]) ? prev[key] : [];
      if (current.includes(value)) return { ...prev, [key]: current.filter(v => v !== value) };
      return { ...prev, [key]: [...current, value] };
    });
  };

  const validateBlocks = () => {
    for (const block of selectedTpl?.blocks || []) {
      if (!block.required) continue;
      const v = selections[block.key];
      if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) {
        return `Vul "${block.label}" in om verder te gaan.`;
      }
    }
    return '';
  };

  const validateContact = () => {
    if (!contact.name.trim()) return 'Naam is verplicht.';
    if (!contact.email.trim()) return 'E-mailadres is verplicht.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email.trim())) return 'Vul een geldig e-mailadres in.';
    return '';
  };

  const handleNext = () => {
    setError('');
    if (step === 1) { const e = validateBlocks(); if (e) { setError(e); return; } }
    if (step === 2) { const e = validateContact(); if (e) { setError(e); return; } }
    setStep(s => s + 1);
  };
  const handleBack = () => { setError(''); setStep(s => Math.max(0, s - 1)); };

  const handleSubmit = async () => {
    setError('');
    setSubmitting(true);
    try {
      const finalSelections = { ...selections };
      Object.entries(customInputs).forEach(([k, v]) => {
        if (v && v.trim()) {
          const existing = finalSelections[k];
          if (Array.isArray(existing)) finalSelections[k] = [...existing, `Anders: ${v.trim()}`];
          else if (!existing) finalSelections[k] = `Anders: ${v.trim()}`;
        }
      });
      await api.submit({
        template_id: selectedTpl.id,
        template_name: selectedTpl.name,
        selections: finalSelections,
        contact,
        notes,
      });
      setStep(4);
    } catch (err) {
      setError(err.response?.data?.detail || 'Versturen mislukt. Probeer het opnieuw.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingList) {
    return (
      <div className={`min-h-screen flex items-center justify-center bg-gradient-to-br ${A.bg} via-white ${A.bg}/30`}>
        <Loader2 className={`w-8 h-8 ${A.text} animate-spin`} />
      </div>
    );
  }

  if (step === 4) {
    return (
      <SuccessView
        onRestart={() => { setStep(0); setSelectedTpl(null); setSelections({}); setContact({ name:'', email:'', phone:'', company:'' }); setNotes(''); }}
        A={A}
        successTitle={successTitle}
        successMessage={successMessage}
        ctaLabel={ctaLabel}
      />
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br ${A.bg} via-white ${A.bg}/30 py-8 px-4 text-slate-800`} data-testid={`${testIdPrefix}-page`}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <img src={LOGO_ICON} alt="Zynq" className="w-10 h-10 rounded-lg" />
            <div>
              <div className="font-bold text-lg leading-none text-slate-900">Zynq</div>
              <div className="text-xs text-slate-500">{kindLabel}</div>
            </div>
          </div>
          {selectedTpl && step > 0 && step < 4 && (
            <button
              onClick={() => { setStep(0); setSelectedTpl(null); setSelections({}); }}
              className={`text-xs text-slate-500 hover:${A.text700} underline`}
            >
              Andere optie kiezen
            </button>
          )}
        </div>

        {step > 0 && step < 4 && <ProgressBar step={step} A={A} />}

        {step === 0 && (
          <PickTemplate
            templates={templates} onPick={handlePickTemplate} error={error}
            A={A} title={title} subtitle={subtitle}
            headlinePrefix={headlinePrefix} headlineHighlight={headlineHighlight} headlineSuffix={headlineSuffix}
            ctaLabel={ctaLabel} testIdPrefix={testIdPrefix}
          />
        )}
        {step === 1 && selectedTpl && (
          <BuildBlocks
            template={selectedTpl} selections={selections} setSelection={setSelection} toggleMulti={toggleMulti}
            customInputs={customInputs} setCustomInputs={setCustomInputs}
            onNext={handleNext} onBack={handleBack} error={error} A={A}
          />
        )}
        {step === 2 && (
          <ContactStep contact={contact} setContact={setContact} notes={notes} setNotes={setNotes} onNext={handleNext} onBack={handleBack} error={error} A={A} />
        )}
        {step === 3 && selectedTpl && (
          <ReviewStep template={selectedTpl} selections={selections} customInputs={customInputs} contact={contact} notes={notes} onSubmit={handleSubmit} onBack={handleBack} submitting={submitting} error={error} A={A} />
        )}

        <div className="mt-10 text-center text-xs text-slate-500">
          <p>Vitaliteit als missie. © Zynq</p>
        </div>
      </div>
    </div>
  );
}

function ProgressBar({ step, A }) {
  const labels = ['Bouw', 'Contact', 'Bevestig'];
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between max-w-md mx-auto">
        {labels.map((lbl, i) => {
          const idx = i + 1;
          const done = step > idx;
          const active = step === idx;
          return (
            <div key={lbl} className="flex items-center flex-1">
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                  done ? `${A.btn.split(' ')[0]} text-white` :
                  active ? `${A.btn.split(' ')[0]} text-white ring-4 ${A.bg100}` :
                  'bg-gray-200 text-slate-500'
                }`}>
                  {done ? <Check className="w-4 h-4" /> : idx}
                </div>
                <span className={`text-[10px] mt-1 font-medium ${active || done ? A.text700 : 'text-slate-400'}`}>{lbl}</span>
              </div>
              {i < labels.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 mb-4 transition-all ${done ? A.btn.split(' ')[0] : 'bg-gray-200'}`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PickTemplate({ templates, onPick, error, A, headlinePrefix, headlineHighlight, headlineSuffix, subtitle, ctaLabel, testIdPrefix }) {
  return (
    <div data-testid="step-pick">
      <div className="text-center mb-8">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2 text-slate-900">
          {headlinePrefix}<span className={A.text}>{headlineHighlight}</span>{headlineSuffix}
        </h1>
        <p className="text-slate-600 max-w-xl mx-auto">{subtitle}</p>
      </div>

      {error && <p className="text-sm text-red-600 text-center mb-4">{error}</p>}

      {templates.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-slate-500">Er zijn momenteel geen opties beschikbaar.</CardContent></Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {templates.map(tpl => {
            const Icon = getIcon(tpl.icon);
            return (
              <button
                key={tpl.id}
                onClick={() => onPick(tpl)}
                data-testid={`${testIdPrefix}-template-${tpl.id}`}
                className={`group text-left bg-white rounded-2xl border-2 border-transparent hover:${A.border500} shadow-sm hover:shadow-lg transition-all p-6 relative overflow-hidden`}
                style={{ borderTopColor: tpl.color || A.hex, borderTopWidth: '4px' }}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                  style={{ background: `${tpl.color || A.hex}15` }}
                >
                  <Icon className="w-6 h-6" style={{ color: tpl.color || A.hex }} />
                </div>
                <h3 className="font-bold text-lg mb-1 text-slate-900">{tpl.name}</h3>
                <p className="text-sm text-slate-600 line-clamp-2 mb-3">{tpl.description}</p>
                <div className={`flex items-center ${A.text700} text-sm font-semibold`}>
                  {ctaLabel} <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BuildBlocks({ template, selections, setSelection, toggleMulti, customInputs, setCustomInputs, onNext, onBack, error, A }) {
  const Icon = getIcon(template.icon);
  return (
    <div data-testid="step-blocks">
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-3"
          style={{ background: `${template.color || A.hex}20`, color: template.color || A.hex }}>
          <Icon className="w-3.5 h-3.5" /> {template.name}
        </div>
        <h2 className="text-2xl font-bold tracking-tight mb-1 text-slate-900">Stel jouw aanbod samen</h2>
        {template.intro_text && <p className="text-slate-600 text-sm max-w-xl mx-auto">{template.intro_text}</p>}
      </div>

      <Card className="border-slate-200 shadow-sm bg-white">
        <CardContent className="p-6 md:p-8 space-y-6">
          {(template.blocks || []).map(block => (
            <BlockRenderer
              key={block.key}
              block={block}
              value={selections[block.key]}
              onChange={setSelection}
              onToggleMulti={toggleMulti}
              customInputs={customInputs}
              setCustomInputs={setCustomInputs}
              A={A}
            />
          ))}
        </CardContent>
      </Card>

      {error && <p className="text-sm text-red-600 mt-4 text-center">{error}</p>}

      <div className="flex justify-between mt-6">
        <Button variant="outline" onClick={onBack} className="gap-2"><ArrowLeft className="w-4 h-4" /> Terug</Button>
        <Button onClick={onNext} data-testid="btn-next-blocks" className={`gap-2 ${A.btn}`}>
          Volgende <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

function BlockRenderer({ block, value, onChange, onToggleMulti, customInputs, setCustomInputs, A }) {
  const customVal = customInputs[block.key] || '';
  const setCustomVal = (v) => setCustomInputs(p => ({ ...p, [block.key]: v }));

  return (
    <div data-testid={`block-${block.key}`}>
      <Label className="text-base font-semibold flex items-center gap-1 text-slate-900">
        {block.label}
        {block.required && <span className="text-red-500">*</span>}
      </Label>
      {block.description && <p className="text-xs text-slate-500 mt-0.5 mb-3">{block.description}</p>}

      {block.type === 'single_choice' && (
        <div className="grid sm:grid-cols-2 gap-2 mt-3">
          {(block.options || []).map(opt => {
            const v = opt.value || opt.label;
            const selected = value === v;
            return (
              <button
                key={v}
                type="button"
                onClick={() => onChange(block.key, v)}
                data-testid={`opt-${block.key}-${v}`}
                className={`text-left p-3 rounded-lg border-2 transition-all ${
                  selected ? `${A.border500} ${A.bg50}` : 'border-slate-200 hover:border-slate-300 bg-white'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="font-semibold text-sm text-slate-900">{opt.label}</div>
                    {opt.description && <div className="text-xs text-slate-500 mt-0.5">{opt.description}</div>}
                  </div>
                  {selected && <Check className={`w-4 h-4 ${A.text} flex-shrink-0 mt-0.5`} />}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {block.type === 'multi_choice' && (
        <div className="grid sm:grid-cols-2 gap-2 mt-3">
          {(block.options || []).map(opt => {
            const v = opt.value || opt.label;
            const arr = Array.isArray(value) ? value : [];
            const selected = arr.includes(v);
            return (
              <button
                key={v}
                type="button"
                onClick={() => onToggleMulti(block.key, v)}
                data-testid={`opt-${block.key}-${v}`}
                className={`text-left p-3 rounded-lg border-2 transition-all ${
                  selected ? `${A.border500} ${A.bg50}` : 'border-slate-200 hover:border-slate-300 bg-white'
                }`}
              >
                <div className="flex items-start gap-2">
                  <div className={`w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center mt-0.5 ${
                    selected ? `${A.border500} ${A.btn.split(' ')[0]}` : 'border-slate-300'
                  }`}>
                    {selected && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-sm text-slate-900">{opt.label}</div>
                    {opt.description && <div className="text-xs text-slate-500 mt-0.5">{opt.description}</div>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {block.type === 'text' && (
        <Input value={value || ''} onChange={(e) => onChange(block.key, e.target.value)} placeholder={block.placeholder || ''} className="mt-3 bg-white text-slate-900 border-slate-300 placeholder:text-slate-400" data-testid={`input-${block.key}`} />
      )}
      {block.type === 'textarea' && (
        <Textarea value={value || ''} onChange={(e) => onChange(block.key, e.target.value)} placeholder={block.placeholder || ''} rows={4} className="mt-3 bg-white text-slate-900 border-slate-300 placeholder:text-slate-400" data-testid={`input-${block.key}`} />
      )}
      {block.type === 'number' && (
        <Input type="number" value={value || ''} onChange={(e) => onChange(block.key, e.target.value)} placeholder={block.placeholder || ''} className="mt-3 max-w-xs bg-white text-slate-900 border-slate-300 placeholder:text-slate-400" data-testid={`input-${block.key}`} />
      )}

      {block.allow_custom && (block.type === 'single_choice' || block.type === 'multi_choice') && (
        <div className="mt-3">
          <Label className="text-xs text-slate-500">Anders / eigen aanvulling</Label>
          <Input value={customVal} onChange={(e) => setCustomVal(e.target.value)} placeholder="Beschrijf je eigen wens..." className="mt-1 bg-white text-slate-900 border-slate-300 placeholder:text-slate-400" data-testid={`custom-${block.key}`} />
        </div>
      )}
    </div>
  );
}

function ContactStep({ contact, setContact, notes, setNotes, onNext, onBack, error, A }) {
  return (
    <div data-testid="step-contact">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold tracking-tight mb-1 text-slate-900">Jouw gegevens</h2>
        <p className="text-slate-600 text-sm">We nemen snel contact op om de details af te stemmen.</p>
      </div>

      <Card className="border-slate-200 shadow-sm bg-white">
        <CardContent className="p-6 md:p-8 space-y-4">
          <div>
            <Label className="text-sm flex items-center gap-1.5 text-slate-800"><User className="w-3.5 h-3.5" /> Naam <span className="text-red-500">*</span></Label>
            <Input value={contact.name} onChange={(e) => setContact(p => ({ ...p, name: e.target.value }))} className="mt-1 bg-white text-slate-900 border-slate-300 placeholder:text-slate-400" data-testid="contact-name" />
          </div>
          <div>
            <Label className="text-sm flex items-center gap-1.5 text-slate-800"><Building className="w-3.5 h-3.5" /> Bedrijf / organisatie</Label>
            <Input value={contact.company} onChange={(e) => setContact(p => ({ ...p, company: e.target.value }))} className="mt-1 bg-white text-slate-900 border-slate-300 placeholder:text-slate-400" data-testid="contact-company" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm flex items-center gap-1.5 text-slate-800"><Mail className="w-3.5 h-3.5" /> E-mail <span className="text-red-500">*</span></Label>
              <Input type="email" value={contact.email} onChange={(e) => setContact(p => ({ ...p, email: e.target.value }))} className="mt-1 bg-white text-slate-900 border-slate-300 placeholder:text-slate-400" data-testid="contact-email" />
            </div>
            <div>
              <Label className="text-sm flex items-center gap-1.5 text-slate-800"><Phone className="w-3.5 h-3.5" /> Telefoon</Label>
              <Input value={contact.phone} onChange={(e) => setContact(p => ({ ...p, phone: e.target.value }))} className="mt-1 bg-white text-slate-900 border-slate-300 placeholder:text-slate-400" placeholder="+31 6 ..." data-testid="contact-phone" />
            </div>
          </div>
          <div>
            <Label className="text-sm flex items-center gap-1.5 text-slate-800"><MessageSquare className="w-3.5 h-3.5" /> Aanvullende opmerkingen</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="mt-1 bg-white text-slate-900 border-slate-300 placeholder:text-slate-400" placeholder="Specifieke wensen, vragen of context..." data-testid="contact-notes" />
          </div>
        </CardContent>
      </Card>

      {error && <p className="text-sm text-red-600 mt-4 text-center">{error}</p>}

      <div className="flex justify-between mt-6">
        <Button variant="outline" onClick={onBack} className="gap-2"><ArrowLeft className="w-4 h-4" /> Terug</Button>
        <Button onClick={onNext} data-testid="btn-next-contact" className={`gap-2 ${A.btn}`}>
          Volgende <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

function ReviewStep({ template, selections, customInputs, contact, notes, onSubmit, onBack, submitting, error, A }) {
  const formatVal = (block, v) => {
    if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) return null;
    const labelFor = (val) => {
      const found = (block.options || []).find(o => (o.value || o.label) === val);
      return found ? found.label : val;
    };
    if (Array.isArray(v)) return v.map(labelFor).join(', ');
    return labelFor(v);
  };

  return (
    <div data-testid="step-review">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold tracking-tight mb-1 text-slate-900">Bevestig je aanvraag</h2>
        <p className="text-slate-600 text-sm">Controleer de samenstelling en verstuur.</p>
      </div>

      <div className="space-y-4">
        <Card className="border-slate-200 bg-white">
          <CardContent className="p-5">
            <h3 className={`font-semibold text-sm ${A.text700} mb-3`}>Jouw aanbod</h3>
            <div className="text-sm font-semibold text-slate-900">{template.name}</div>
            {(template.blocks || []).map(block => {
              const val = formatVal(block, selections[block.key]);
              const custom = customInputs[block.key];
              if (!val && !custom) return null;
              return (
                <div key={block.key} className="mt-3 pt-3 border-t border-slate-100 first-of-type:border-t-0 first-of-type:pt-3">
                  <div className="text-xs text-slate-500">{block.label}</div>
                  <div className="text-sm font-medium mt-0.5 text-slate-800">
                    {val}
                    {custom && <span className="text-slate-500 italic"> {val ? '+ ' : ''}Anders: {custom}</span>}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        <Card className="border-slate-200 bg-white">
          <CardContent className="p-5">
            <h3 className={`font-semibold text-sm ${A.text700} mb-3`}>Contactgegevens</h3>
            <div className="grid sm:grid-cols-2 gap-2 text-sm">
              <div><span className="text-slate-500">Naam:</span> <span className="font-semibold text-slate-900">{contact.name}</span></div>
              {contact.company && <div><span className="text-slate-500">Bedrijf:</span> <span className="font-semibold text-slate-900">{contact.company}</span></div>}
              <div><span className="text-slate-500">E-mail:</span> <span className="font-semibold text-slate-900">{contact.email}</span></div>
              {contact.phone && <div><span className="text-slate-500">Telefoon:</span> <span className="font-semibold text-slate-900">{contact.phone}</span></div>}
            </div>
            {notes && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <div className="text-xs text-slate-500">Opmerkingen</div>
                <div className="text-sm mt-0.5 whitespace-pre-wrap text-slate-800">{notes}</div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {error && <p className="text-sm text-red-600 mt-4 text-center">{error}</p>}

      <div className="flex justify-between mt-6">
        <Button variant="outline" onClick={onBack} disabled={submitting} className="gap-2"><ArrowLeft className="w-4 h-4" /> Terug</Button>
        <Button onClick={onSubmit} disabled={submitting} data-testid="btn-submit" className={`gap-2 ${A.btn} min-w-[180px]`}>
          {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Versturen...</>) : (<>Aanvraag versturen <Send className="w-4 h-4" /></>)}
        </Button>
      </div>
    </div>
  );
}

function SuccessView({ onRestart, A, successTitle, successMessage, ctaLabel }) {
  return (
    <div className={`min-h-screen bg-gradient-to-br ${A.bg} via-white ${A.bg}/30 flex items-center justify-center p-4 text-slate-800`} data-testid="step-success">
      <Card className="max-w-md w-full border-slate-200 shadow-lg bg-white">
        <CardContent className="p-8 text-center space-y-4">
          <div className={`w-20 h-20 rounded-full ${A.bg100} flex items-center justify-center mx-auto`}>
            <CheckCircle2 className={`w-10 h-10 ${A.text}`} />
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900">{successTitle}</h2>
          <p className="text-slate-600 text-sm">{successMessage}</p>
          <div className="pt-3">
            <Button onClick={onRestart} variant="outline" className="gap-2">
              <Sparkles className="w-4 h-4" /> Nog een aanvraag bouwen
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
